import React from "react";
import {
  FaUserEdit,
  FaFileInvoice,
  FaMoneyCheckAlt,
  FaDraftingCompass,
  FaHandshake,
  FaTools,
  FaClipboardCheck,
  FaCalculator,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { canAccess } from "../hooks/usePermission";

const Home = ({ selectedProject }) => {
  const navigate = useNavigate();

  // ============================================================
  // PROJECT STATUS
  // ============================================================

  const isAfterBooking = Boolean(selectedProject?.hasPayment);

  // ============================================================
  // LOGGED-IN USER
  // ============================================================

  let user = {};

  try {
    user = JSON.parse(localStorage.getItem("user") || "{}");
  } catch (error) {
    user = {};
  }

  const userRole = String(
    user?.role || user?.roleName || localStorage.getItem("userRole") || "",
  )
    .trim()
    .toLowerCase();

  const isAdmin = userRole === "admin";

  // ============================================================
  // ROLE BASED SPECIAL ACCESS
  // ============================================================

  const financeRoles = ["admin", "head_accountant", "accountant"];

  const vendorRoles = [
    "admin",
    "project_manager",
    "head_accountant",
    "accountant",
  ];

  const canSeeFinance = financeRoles.includes(userRole);
  const canSeeVendor = vendorRoles.includes(userRole);

  // ============================================================
  // CARD PERMISSIONS
  // ============================================================

  const canClientDetail = isAdmin || canAccess("home", "clientDetail");

  const canQuote = isAdmin || canAccess("home", "quote");

  const canPaymentDetail =
    isAdmin || canSeeFinance || canAccess("home", "paymentDetail");

  const canDrawings3D = isAdmin || canAccess("home", "drawings3D");

  const canVendorQuote =
    isAdmin || canSeeVendor || canAccess("home", "vendorQuote");

  const canRecce = isAdmin || canAccess("home", "recce");

  const canSiteProgress = isAdmin || canAccess("home", "siteProgress");

  const canVendorEstimate =
    isAdmin || canSeeVendor || canAccess("home", "vendorEstimate");

  // ============================================================
  // PAGE
  // ============================================================

  return (
    <div
      style={{
        padding: "30px",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* ========================================================
          HEADER
      ======================================================== */}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "relative",
          borderBottom: "1px solid #dbeafe",
          paddingBottom: "10px",
          marginBottom: "25px",
        }}
      >
        <button
          type="button"
          onClick={() => navigate("/")}
          style={{
            background: "#fff",
            color: "#000",
            padding: "6px 14px",
            border: "1px solid #ccc",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: "600",
          }}
        >
          ← Back
        </button>

        <h2
          style={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            margin: 0,
            fontSize: "22px",
            fontWeight: "700",
            color: "#111827",
            whiteSpace: "nowrap",
          }}
        >
          Project Dashboard
        </h2>

        <div style={{ width: "100px" }} />
      </div>

      {/* ========================================================
          PROJECT NAME
      ======================================================== */}

      <h2
        style={{
          textAlign: "center",
          marginBottom: "40px",
          color: "#343A76",
          fontSize: "42px",
          fontWeight: "bold",
          textTransform: "capitalize",
        }}
      >
        {selectedProject?.name || "Project Name"}
      </h2>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: "40px",
          maxWidth: "1100px",
          margin: "0 auto",
        }}
      >
        {/* ======================================================
            1. CLIENT DETAIL
        ====================================================== */}

        {canClientDetail && (
          <Card onClick={() => navigate("/client-detail")}>
            <FaUserEdit
              size={60}
              style={{
                marginBottom: "18px",
                color: "#007bff",
              }}
            />

            <h3 style={cardTitle}>Client Detail</h3>

            <p style={cardDescription}>Edit Client Information</p>
          </Card>
        )}

        {/* ======================================================
            2. DRAWINGS & 3D
            MOVED TO QUOTE'S OLD POSITION
        ====================================================== */}

        {isAfterBooking && canDrawings3D && (
          <Card onClick={() => navigate("/3dDrawings")}>
            <FaDraftingCompass
              size={60}
              style={{
                marginBottom: "18px",
                color: "#6f42c1",
              }}
            />

            <h3 style={cardTitle}>Drawings & 3D</h3>

            <p style={cardDescription}>3D Drawings</p>
          </Card>
        )}

        {/* ======================================================
            3. RECCE
            MOVED TO PAYMENT DETAIL'S OLD POSITION
        ====================================================== */}

        {isAfterBooking && canRecce && (
          <Card onClick={() => navigate("/recce-module")}>
            <FaClipboardCheck
              size={60}
              style={{
                marginBottom: "18px",
                color: "#ff5722",
              }}
            />

            <h3 style={cardTitle}>Recce</h3>

            <p style={cardDescription}>Manage Recce Updates</p>
          </Card>
        )}

        {/* ======================================================
            4. QUOTE
            MOVED TO DRAWINGS & 3D'S OLD POSITION
        ====================================================== */}

        {canQuote && userRole !== "project_manager" && (
          <Card onClick={() => navigate("/quote")}>
            <FaFileInvoice
              size={60}
              style={{
                marginBottom: "18px",
                color: "#28a745",
              }}
            />

            <h3 style={cardTitle}>Quote</h3>

            <p style={cardDescription}>Create / Edit Quote</p>
          </Card>
        )}

        {/* ======================================================
            5. VENDOR COST & PAYMENTS
            POSITION REMAINS SAME
        ====================================================== */}

        {isAfterBooking && canVendorQuote && (
          <Card onClick={() => navigate("/VendorQuote")}>
            <FaHandshake
              size={60}
              style={{
                marginBottom: "18px",
                color: "#17a2b8",
              }}
            />

            <h3 style={cardTitle}>Vendor Cost & Payments</h3>

            <p style={cardDescription}>Manage Vendor Costs & Quotes</p>
          </Card>
        )}

        {/* ======================================================
            6. PAYMENT DETAIL
            MOVED TO RECCE'S OLD POSITION
        ====================================================== */}

        {canPaymentDetail && (
          <Card onClick={() => navigate("/PaymentDetail")}>
            <FaMoneyCheckAlt
              size={60}
              style={{
                marginBottom: "18px",
                color: "#ffc107",
              }}
            />

            <h3 style={cardTitle}>P & L</h3>

            <p style={cardDescription}>Add Payment Information</p>
          </Card>
        )}

        {/* ======================================================
            7. SITE PROGRESS
            NOTHING DELETED
        ====================================================== */}

        {isAfterBooking && canSiteProgress && (
          <Card onClick={() => navigate("/SiteProgress")}>
            <FaTools
              size={60}
              style={{
                marginBottom: "18px",
                color: "#dc3545",
              }}
            />

            <h3 style={cardTitle}>Site Progress</h3>

            <p style={cardDescription}>Track Site Progress Updates</p>
          </Card>
        )}

        {/* ======================================================
            8. VENDOR ESTIMATE
            NOTHING DELETED
        ====================================================== */}

        {isAfterBooking && canVendorEstimate && (
          <Card onClick={() => navigate("/vendor-estimate")}>
            <FaCalculator
              size={60}
              style={{
                marginBottom: "18px",
                color: "#20c997",
              }}
            />

            <h3 style={cardTitle}>Vendor Estimate</h3>

            <p style={cardDescription}>Track Vendor Estimate Updates</p>
          </Card>
        )}
      </div>

      {/* ========================================================
          NO CARD PERMISSION
      ======================================================== */}

      {!canClientDetail &&
        !canQuote &&
        !canPaymentDetail &&
        (!isAfterBooking ||
          (!canDrawings3D &&
            !canVendorQuote &&
            !canRecce &&
            !canSiteProgress &&
            !canVendorEstimate)) && (
          <div
            style={{
              textAlign: "center",
              marginTop: "40px",
              color: "#64748b",
              fontSize: "15px",
              fontWeight: "600",
            }}
          >
            No modules are available for your current permissions.
          </div>
        )}
    </div>
  );
};

// ============================================================
// CARD STYLE
// ============================================================

const cardStyle = {
  width: "320px",
  height: "260px",
  border: "1px solid rgba(0,0,0,0.08)",
  borderRadius: "16px",
  background: "#ffffffee",
  boxShadow: "0 8px 20px rgba(0, 0, 0, 0.08)",
  backdropFilter: "blur(6px)",
  WebkitBackdropFilter: "blur(6px)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  transition: "transform 0.25s ease, box-shadow 0.25s ease",
  boxSizing: "border-box",
};

// ============================================================
// CARD HOVER STYLE
// ============================================================

const cardHoverStyle = {
  transform: "translateY(-6px)",
  boxShadow: "0 12px 28px rgba(0,0,0,0.12)",
};

// ============================================================
// CARD TITLE
// ============================================================

const cardTitle = {
  fontSize: "24px",
  fontWeight: "600",
  color: "#343A76",
  margin: "0 0 4px 0",
  textAlign: "center",
};

// ============================================================
// CARD DESCRIPTION
// ============================================================

const cardDescription = {
  fontSize: "16px",
  color: "#555",
  textAlign: "center",
  padding: "0 10px",
  lineHeight: "22px",
  margin: 0,
};

// ============================================================
// CARD COMPONENT
// ============================================================

const Card = ({ children, ...props }) => {
  const [hover, setHover] = React.useState(false);

  return (
    <div
      style={{
        ...cardStyle,
        ...(hover ? cardHoverStyle : {}),
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      {...props}
    >
      {children}
    </div>
  );
};

export default Home;
